/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useContext, useState } from "react";
import PropTypes from "prop-types";
import { withRouter } from "react-router-dom";
import { Button } from "@material-ui/core";
import Page from "components/common_components/Page/Page";
import useStyles from "../useStyles";
import { useDispatch, useSelector } from "react-redux";
import { notifyError } from "redux/actions/Notifications";
import LoadingPage from "components/common_components/LoadingPage";
import ModulesContext from "../ModulesContext";
import Grid from "@material-ui/core/Grid";
import PlayCircleFilledWhiteIcon from "@material-ui/icons/PlayCircleFilledWhite";
import { hasElements } from "utils/arrays";
import Chat from "components/Chat";
import ChatInitializerContext from "components/Chat/ChatInitializerContext";
import AppContext from "AppContext";
import { getAttributes } from "components/Chat/utils";
import { getEnvVar } from "utils/env";

function ChatInitializer({ onCancel }) {
    const classes = useStyles();
    const dispatch = useDispatch();
    const { currentFileCase, isLoading, startConversationAsync, clientPhoto } = useContext(ModulesContext);
    const { user } = useSelector((state) => state.Login);
    const [resources, setResources] = useState({ users: [] });
    const clientHasConversations = hasElements(currentFileCase.client.conversations);

    const {
        api: {
            Settings: { Users },
        },
        messaging: {
            twilioConversations: { client },
        },
        conversations,
        setConversations,
        changeChatProfile,
    } = useContext(AppContext);

    function getUsers() {
        Users.getAll({
            onSuccess: (data) => {
                setResources((prev) => ({
                    ...prev,
                    users: data,
                }));
            },
            onError: (error) => {
                setResources((prev) => ({
                    ...prev,
                    users: [],
                }));
                dispatch(notifyError(error));
            },
        });
    }

    useEffect(() => {
        getUsers();
        changeChatProfile(getEnvVar("REACT_APP_CHAT_IDENTITY"));

        return () => {
            setResources((prev) => ({
                users: [],
            }));
            changeChatProfile(undefined);
        };
    }, []);

    useEffect(() => {
        if (!currentFileCase) {
            dispatch(notifyError("You have to select a client file first!"));
            onCancel();
        } else {
            const getNonActiveClientConversationsAsync = async () => {
                const nonActiveClientConversations = getNonActiveClientConversations();
                if (hasElements(nonActiveClientConversations) && client) {
                    const promises = nonActiveClientConversations.map((x) => client.peekConversationBySid(x.sid));
                    const responses = await Promise.all(promises);
                    if (hasElements(responses)) {
                        setConversations((prev) => {
                            const toAdd = responses.filter(x => !prev.some(c => c.sid === x.sid))
                            return [...prev, ...toAdd];
                        });
                    }
                }
            };
            getNonActiveClientConversationsAsync();
        }
    }, [currentFileCase, client]);

    const startNewConversation = () => startConversationAsync(user.id);

    const resolveInternalUserLabel = (email) => {
        const authorUser = resources.users.find((u) => u.email === email);
        if (authorUser) {
            return `${String(authorUser.name).trim()}`;
        }
        return `Unknown User (${email})`;
    };

    const resolveClientUserLabel = () => {
        return currentFileCase.client.fullName;
    };

    const authorLabelResolver = (message) => {
        const { attributes } = message;
        if (attributes && attributes.senderUserEmail) {
            const { senderUserEmail } = attributes;
            return resolveInternalUserLabel(senderUserEmail);
        }
        return resolveClientUserLabel();
    };

    const clientPhotoResolver = (conversation) => {
        const { type } = getAttributes(conversation);

        if (type === "external") {
            return clientPhoto;
        }
    };

    const conversationNameResolver = async (conversation) => {
        const { type, clientName } = getAttributes(conversation);
        return type === "external" ? clientName : conversation.friendlyName;
    };

    const getActiveClientConversation = () => {
        return clientHasConversations ? currentFileCase.client.conversations.find((x) => x.state === "active") : null;
    };

    const getNonActiveClientConversations = () => {
        return clientHasConversations ? currentFileCase.client.conversations.filter((x) => x.state !== "active") : [];
    };

    const getActiveConversation = () => {
        const activeClientConversation = getActiveClientConversation();
        if (activeClientConversation && hasElements(conversations)) {
            return conversations.find((x) => x.sid === activeClientConversation.sid);
        }
        return null;
    };

    const getNonActiveConversations = () => {
        const nonActiveClientConversations = getNonActiveClientConversations();

        if (hasElements(conversations) && hasElements(nonActiveClientConversations)) {
            return conversations.filter((x) => nonActiveClientConversations.map((c) => c.sid).includes(x.sid));
        }

        return [];
    };

    const conversationsGetter = () => {
        const conversation = getActiveConversation();
        const nonActiveConversations = getNonActiveConversations();
        return conversation ? [conversation, ...nonActiveConversations] : conversations;
    };

    const hasValidConversation = Boolean(getActiveConversation());
    const hasAnyConversation = hasValidConversation || getNonActiveConversations().length > 0;

    return (
        <Page className={classes.root}>
            {isLoading ? (
                <LoadingPage />
            ) : (
                <>
                    <Grid container spacing={3} style={{ justifyContent: "space-between" }}>
                        <Grid item xs={6}>
                            {!hasValidConversation && (
                                <Button
                                    variant="text"
                                    size="small"
                                    color="primary"
                                    startIcon={<PlayCircleFilledWhiteIcon />}
                                    onClick={startNewConversation}
                                >
                                    Start conversation
                                </Button>
                            )}
                        </Grid>
                        <Grid item xs={12} className="chat-disclaimer-container">
                            <p className="disclaimer">
                                This chat window is not exclusive for this case, it is directly related to the client's phone number,
                                therefore you will see the same conversation in all cases from the same client.
                            </p>
                        </Grid>
                        {hasAnyConversation && (
                            <Grid item xs={12} className="chat-in-grid">
                                <ChatInitializerContext.Provider
                                    value={{ authorLabelResolver, clientPhotoResolver, conversationNameResolver, conversationsGetter }}
                                >
                                    <Chat isSingleConversation />
                                </ChatInitializerContext.Provider>
                            </Grid>
                        )}
                    </Grid>
                </>
            )}
        </Page>
    );
}

ChatInitializer.propTypes = {
    onCancel: PropTypes.func,
};

ChatInitializer.defaultProps = {
    onCancel: () => {},
};

export default withRouter(ChatInitializer);
